<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import moment from 'moment-jalaali'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import {
  updateInternationalAccountAction,
  updateInternationalPaypalAccountAction,
  updateInternationalRevolutAccountAction,
  updateInternationalWiseAccountAction,
  updateIranianAccountAction,
  updateIranianSrcAccountAction,
} from '@/api/functions/bank-account/update-bank-account.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const props = defineProps({
  modelValue: Boolean,
  account: {
    type: Object,
    required: true,
  },
  userId: { type: Number },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()

// Reactive data
const accountType = ref('')
const internationalType = ref('')
const loading = ref(false)

// Options
const poundOptions = [
  { label: 'بله', value: true },
  { label: 'خیر', value: false },
]

const dayOptions = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const monthOptions = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const yearOptions = computed(() =>
  Array.from({ length: 104 }, (_, i) => ({
    label: (1403 - i).toString(),
    value: (1403 - i).toString(),
  }))
)

// Validation Schema
const createValidationSchema = () => {
  const baseInternationalSchema = {
    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام بانک باید فقط حروف انگلیسی باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام دارنده حساب باید فقط حروف انگلیسی باشد'),

    country: yup.string().required('کشور الزامی است'),
  }

  const baseDomesticSchema = {
    sheba: yup
      .string()
      .required('شماره شبا الزامی است')
      .matches(/^[0-9]{24}$/, 'شماره شبا باید ۲۴ رقم باشد'),

    cardNumber: yup
      .string()
      .required('شماره کارت الزامی است')
      .length(16, 'شماره کارت باید ۱۶ رقمی باشد')
      .matches(/^[0-9]+$/, 'شماره کارت باید فقط عدد باشد'),

    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد'),

    nationalCode: yup
      .string()
      .required('کد ملی الزامی است')
      .length(10, 'کد ملی باید ۱۰ رقمی باشد')
      .matches(/^[0-9]+$/, 'کد ملی باید فقط عدد باشد'),
  }

  if (accountType.value === 'بین المللی') {
    if (internationalType.value === 'بین بانکی') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
        isPound: yup.boolean().nullable(),
        sortCode: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Sort Code برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),
        accountNumber: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Account Number برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    } else if (internationalType.value === 'رولوت') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
        revolutTag: yup
          .string()
          .required('Revolut Tag الزامی است')
          .min(2, 'Revolut Tag باید حداقل ۲ کاراکتر باشد')
          .max(50, 'Revolut Tag باید حداکثر ۵۰ کاراکتر باشد'),
      })
    } else if (internationalType.value === 'وایز') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
        wiseTag: yup.string().nullable().max(50, 'Wise Tag باید حداکثر ۵۰ کاراکتر باشد'),
      })
    } else if (internationalType.value === 'پی پال') {
      return yup.object({
        ...baseInternationalSchema,
        paypalEmail: yup.string().required('ایمیل Paypal الزامی است').email('فرمت ایمیل معتبر نیست'),
        paypalId: yup
          .string()
          .required('ایمیل Paypal الزامی است')
          .nullable()
          .max(50, 'Paypal ID باید حداکثر ۵۰ کاراکتر باشد'),
      })
    }
  } else if (accountType.value === 'مقصد ریالی' || accountType.value === 'مبداء ریالی') {
    const domesticSchema = { ...baseDomesticSchema }

    return yup.object({
      ...domesticSchema,
      birthDate: yup.object({
        day: yup.string().required('روز تولد الزامی است'),
        month: yup.string().required('ماه تولد الزامی است'),
        year: yup.string().required('سال تولد الزامی است'),
      }),
    })
  }

  return yup.object({})
}
// Reactive validation schema
const validationSchema = computed(() => createValidationSchema())

// Form setup
const { handleSubmit, resetForm, errors, setValues } = useForm({
  validationSchema,
  initialValues: {
    paypalEmail: '',
    paypalId: '',
    iban: '',
    bic: '',
    bankName: '',
    ownerName: '',
    country: '',
    isPound: null,
    sortCode: '',
    accountNumber: '',
    revolutTag: '',
    wiseTag: '',
    sheba: '',
    cardNumber: '',
    nationalCode: '',
    birthDate: {
      day: '',
      month: '',
      year: '',
    },
  },
})
// Fields - International
const { value: paypalEmail } = useField('paypalEmail')
const { value: paypalId } = useField('paypalId')
const { value: iban } = useField('iban')
const { value: bic } = useField('bic')
const { value: bankName } = useField('bankName')
const { value: ownerName } = useField('ownerName')
const { value: country } = useField('country')
const { value: isPound } = useField('isPound')
const { value: sortCode } = useField('sortCode')
const { value: accountNumber } = useField('accountNumber')
const { value: revolutTag } = useField('revolutTag')
const { value: wiseTag } = useField('wiseTag')

// Fields - Domestic
const { value: sheba } = useField('sheba')
const { value: cardNumber } = useField('cardNumber')
const { value: nationalCode } = useField('nationalCode')
const { value: birthDate } = useField<{ day: string; month: string; year: string }>('birthDate')

// Computed fields for dynamic labels
const ibanField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalEmail.value : iban.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalEmail.value = value
    } else {
      iban.value = value
    }
  },
})

const bicField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalId.value : bic.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalId.value = value
    } else {
      bic.value = value
    }
  },
})

// Mutation for international accounts
const { mutate: updateIranianAccount } = useMutationApi<unknown, { userId: string; accountId: any; payload: any }>({
  mutationKey: ['updateIranianAccount'],
  mutationFn: ({ userId, accountId, payload }) => updateIranianAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مقصد ریالی با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const { mutate: updateIranianSrcAccount } = useMutationApi<unknown, { userId: string; accountId: any; payload: any }>({
  mutationKey: ['updateIranianSrcAccount'],
  mutationFn: ({ userId, accountId, payload }) => updateIranianSrcAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مبداء ریالی با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const { mutate: updateInternationalAccount } = useMutationApi<
  unknown,
  { userId: string; accountId: any; payload: any }
>({
  mutationKey: ['updateInternationalAccount'],
  mutationFn: ({ userId, accountId, payload }) => updateInternationalAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب بین‌المللی با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const { mutate: updateInternationalRevolutAccount } = useMutationApi<
  unknown,
  { userId: string; accountId: any; payload: any }
>({
  mutationKey: ['updateInternationalRevolutAccount'],
  mutationFn: ({ userId, accountId, payload }) =>
    updateInternationalRevolutAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب رولوت با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const { mutate: updateInternationalWiseAccount } = useMutationApi<
  unknown,
  { userId: string; accountId: any; payload: any }
>({
  mutationKey: ['updateInternationalWiseAccount'],
  mutationFn: ({ userId, accountId, payload }) =>
    updateInternationalWiseAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب ویز با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const { mutate: updateInternationalPaypalAccount } = useMutationApi<
  unknown,
  { userId: string; accountId: any; payload: any }
>({
  mutationKey: ['updateInternationalPaypalAccount'],
  mutationFn: ({ userId, accountId, payload }) =>
    updateInternationalPaypalAccountAction(userId, accountId, { ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب پی‌پال با موفقیت ویرایش شد', 'موفقیت')
    emit('update:modelValue', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const submitForm = handleSubmit((values) => {
  loading.value = true

  try {
    switch (accountType.value) {
      // ===== بین‌المللی =====
      case 'بین المللی':
        switch (internationalType.value) {
          case 'بین بانکی': {
            const bankPayload: any = {
              iban_number: values.iban,
              bic_number: values.bic,
              bank_name: values.bankName,
              owner_full_name: values.ownerName,
              country_id: values.country,
            }
            if (values.isPound) {
              bankPayload.sort_code = values.sortCode
              bankPayload.account_number = values.accountNumber
              bankPayload.is_pound = true
            }
            updateInternationalAccount({
              userId: props?.userId?.toString() ?? '',
              accountId: props.account.id,
              payload: bankPayload,
            })
            break
          }

          case 'رولوت':
            updateInternationalRevolutAccount({
              userId: props?.userId?.toString() ?? '',
              accountId: props.account.id,
              payload: {
                iban_number: values.iban,
                bic_number: values.bic,
                bank_name: values.bankName,
                owner_full_name: values.ownerName,
                country_id: values.country,
                revolut_tag: values.revolutTag,
              },
            })
            break

          case 'پی پال':
            updateInternationalPaypalAccount({
              userId: props?.userId?.toString() ?? '',
              accountId: props.account.id,
              payload: {
                paypal_email: values.paypalEmail,
                paypal_id: values.paypalId || undefined,
                bank_name: values.bankName,
                owner_full_name: values.ownerName,
                country_id: values.country,
              },
            })
            break
          case 'وایز':
            updateInternationalWiseAccount({
              userId: props?.userId?.toString() ?? '',
              accountId: props.account.id,
              payload: {
                iban_number: values.iban,
                bic_number: values.bic,
                bank_name: values.bankName,
                owner_full_name: values.ownerName,
                country_id: values.country,
                wise_tag: values.wiseTag || undefined,
              },
            })
            break
        }
        break

      // ===== ریالی =====
      case 'مقصد ریالی':
      case 'مبداء ریالی': {
        const shebaWithIR = `IR${values.sheba}`
        const jalaaliDateString = `${values.birthDate.year}/${values.birthDate.month}/${values.birthDate.day}`
        const gregorianDate = moment(jalaaliDateString, 'jYYYY/jMM/jDD').format('YYYY-MM-DD')

        if (accountType.value === 'مقصد ریالی') {
          updateIranianAccount({
            userId: props?.userId?.toString() ?? '',
            accountId: props.account.id,
            payload: {
              sheba_number: shebaWithIR,
              card_number: values.cardNumber,
              bank_name: values.bankName,
              owner_full_name: values.ownerName,
              national_code: values.nationalCode,
              birth_date: gregorianDate,
            },
          })
        } else {
          updateIranianAccount({
            userId: props?.userId?.toString() ?? '',
            accountId: props.account.id,
            payload: {
              sheba_number: shebaWithIR,
              card_number: values.cardNumber,
              bank_name: values.bankName,
              owner_full_name: values.ownerName,
              national_code: values.nationalCode,
              birth_date: gregorianDate,
            },
          })
        }
        break
      }
    }
  } catch (error) {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  }
})

const filterEnglishCharacters = (event: Event) => {
  const input = event.target as HTMLInputElement
  const englishOnlyRegex = /^[a-zA-Z ]*$/
  if (!englishOnlyRegex.test(input.value)) {
    input.value = input.value.replace(/[^a-zA-Z ]/g, '')
  }
}

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

watch(
  () => props.account,
  (newAccount) => {
    if (!newAccount) return

    resetForm()

    if (newAccount.isIranian) {
      if (newAccount.iranianBankType === 'destinationRial') {
        accountType.value = 'مقصد ریالی'
      } else {
        accountType.value = 'مبداء ریالی'
      }

      setValues({
        sheba: newAccount.ibanOrShebaNumber?.replace(/^IR/i, '') || '',
        cardNumber: newAccount.bicOrCardNumber || '',
        bankName: newAccount.bankName || '',
        ownerName: newAccount.ownerFullName || '',
        nationalCode: newAccount.nationalCode || '',
        birthDate: (() => {
          if (!newAccount.birthDate) return { day: '', month: '', year: '' }

          const [year, month, day] = newAccount.birthDate.split('-')

          const jalaliDate = moment(newAccount.birthDate, 'YYYY-MM-DD')

          return {
            day,
            month,
            year: jalaliDate.format('jYYYY'),
          }
        })(),
      })
    } else {
      accountType.value = 'بین المللی'

      switch (newAccount.type) {
        case 'international':
          internationalType.value = 'بین بانکی'
          break
        case 'revolut':
          internationalType.value = 'رولوت'
          break
        case 'paypal':
          internationalType.value = 'پی پال'
          break
        case 'wise':
          internationalType.value = 'وایز'
          break
      }

      setValues({
        iban: newAccount.ibanOrShebaNumber || '',
        bic: newAccount.bicOrCardNumber || '',
        bankName: newAccount.bankName || '',
        ownerName: newAccount.ownerFullName || '',
        country: newAccount.countryCuid || newAccount.countryUuid || '',
        revolutTag: newAccount.revolutTag || '',
        wiseTag: newAccount.wiseTag || '',
        paypalEmail: newAccount.paypalEmail || '',
        paypalId: newAccount.paypalId || '',
        isPound: newAccount.isPound ?? null,
        sortCode: newAccount.bankAccountSortCode || '',
        accountNumber: newAccount.bankAccountNumber || '',
      })
    }
  },
  { immediate: true }
)
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="650px"
    background="#FFF" :closable="true" title="ویرایش حساب" closeDirection="left" class="update-request">
    <form @submit.prevent="submitForm" class="flex flex-col min-h-[530px]">
      <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">
        <div>
          <!-- Info Banner -->
          <div v-if="accountType && (accountType !== 'بین المللی' || internationalType)" class="mb-10">
            <div class="bg-[#faf2db] rounded-xl p-2.5 text-black text-sm font-normal">
              <p v-if="accountType === 'بین المللی'">
                حساب بانکی که خریدار قصد دارد مبلغ ارزی به آن واریز شود برای مثال (حساب بانکی، پی
                پال، رولوت، وایز و ...)
              </p>
              <p v-if="accountType === 'مقصد ریالی'">
                حساب بانکی که فروشنده قصد دارد تسویه ریالی توسط پی جیب، با آن حساب انجام شود.
              </p>
              <p v-if="accountType === 'مبداء ریالی'">
                حساب بانکی که خریدار از طریق این حساب قصد واریز مبلغ ریالی به پی جیب را دارد.
              </p>
            </div>
          </div>

          <!-- Form -->
          <div v-if="accountType && (accountType !== 'بین المللی' || internationalType)" class="space-y-6">
            <!-- International Account Fields -->
            <div v-if="accountType === 'بین المللی'">
              <!-- PayPal Fields -->
              <div v-if="internationalType === 'پی پال'" class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="ibanField" name="ibanField" label="ایمیل Paypal را وارد کنید" type="email"
                    placeholder="example@paypal.com" />
                  <div v-if="errors.paypalEmail"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.paypalEmail }}</span>
                  </div>
                </div>
                <div>
                  <Input v-model="bicField" name="bicField" :isTag="true" label="Paypal Id را وارد کنید" type="text"
                    placeholder="PID123456789" />
                  <div v-if="errors.paypalId"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.paypalId }}</span>
                  </div>
                </div>
              </div>

              <!-- IBAN & BIC Fields -->
              <div v-if="internationalType === 'بین بانکی' || internationalType === 'رولوت' || internationalType === 'وایز'"
                class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="ibanField" name="ibanField" label="شماره IBAN را وارد کنید" type="text"
                    placeholder="IE84REVO99036020197146" />
                  <div v-if="errors.iban" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.iban }}</span>
                  </div>
                </div>
                <div>
                  <Input v-model="bicField" name="bicField" label="شماره BIC CODE را وارد کنید" type="text"
                    placeholder="REVOIE2D" />
                  <div v-if="errors.bic" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.bic }}</span>
                  </div>
                </div>
              </div>

              <!-- Common International Fields -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="bankName" name="bankName" @input="filterEnglishCharacters"
                    label="نام بانک را به انگلیسی وارد کنید" type="text" placeholder="Bank Name" />
                </div>
                <div>
                  <Input v-model="ownerName" name="ownerName" @input="filterEnglishCharacters"
                    label="نام دارنده حساب را به انگلیسی وارد کنید" type="text" placeholder="Full Name" />
                </div>
              </div>

              <div class="flex flex-col lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <CountrySelect v-model="country" name="country" label="کشور مقصد حساب ارزی خود را انتخاب کنید" />
                  <div v-if="errors.country" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.country }}</span>
                  </div>
                </div>

                <div v-if="internationalType === 'بین بانکی'" class="pt-3">
                  <Radio v-model="isPound" name="isPound" label="آیا حساب شما پوند است؟" :options="poundOptions" />
                </div>

                <!-- Bank Transfer Specific Fields -->
                <div v-if="isPound === true && internationalType === 'بین بانکی'"
                  class="col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <Input v-model="accountNumber" name="accountNumber" label="مقدار Account Number را وارد کنید"
                      type="text" placeholder="12345678" />
                  </div>
                  <div>
                    <Input v-model="sortCode" name="sortCode" label="مقدار Sort Code را وارد کنید" type="text"
                      placeholder="123456" />
                  </div>
                </div>
                <!-- Revolut Specific Field -->
                <div v-if="internationalType === 'رولوت'">
                  <Input v-model="revolutTag" :isTag="true" name="revolutTag" label="Revolut Tag را وارد کنید"
                    type="text" placeholder="@username" />
                </div>
                <!-- Wise Specific Field -->
                <div v-if="internationalType === 'وایز'">
                  <Input v-model="wiseTag" :isTag="true" name="wiseTag" label="Wise Tag را وارد کنید (اختیاری)"
                    type="text" placeholder="@username" />
                </div>
              </div>
            </div>

            <!-- Domestic Account Fields -->
            <div v-if="accountType === 'مقصد ریالی' || accountType === 'مبداء ریالی'">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="sheba" name="sheba" :isSheba="true" label="شماره شبا را وارد کنید" type="text"
                    placeholder="060120000000009886055009" />
                </div>
                <div>
                  <Input v-model="cardNumber" name="cardNumber" label="شماره کارت را وارد کنید" type="text"
                    placeholder="6104338650180389" />
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="bankName" name="bankName" label="نام بانک را وارد کنید" type="text"
                    placeholder="ملی تست" />
                </div>
                <div>
                  <Input v-model="ownerName" name="ownerName" label="نام دارنده حساب را وارد کنید" type="text"
                   />
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Input v-model="nationalCode" name="nationalCode" label="کد ملی دارنده حساب را وارد کنید" type="text"
                   />
                </div>
                <div>
                  <div class="grid lg:grid-cols-3 gap-4">
                    <Select v-model="birthDate.day" name="birthDate.day" :options="dayOptions" label="روز تولد" />
                    <Select v-model="birthDate.month" name="birthDate.month" :options="monthOptions" label="ماه تولد" />
                    <Select v-model="birthDate.year" name="birthDate.year" :options="yearOptions" label="سال تولد" />
                  </div>
                  <div v-if="errors.birthDate"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>تاریخ تولد الزامی است</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="تایید و ویرایش حساب" :isAdmin="true" class="w-9/12 h-12 rounded-xl" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" />
      </div>
    </form>
  </Modal>
</template>
